import { Badge, Box, HStack, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { SourcingEventDto, useGetAwardLettersQuery, useListBidsForEventQuery } from "../../../autogen/bff-api";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { ListLoadError } from "../../../common/ListLoadError";
import { WithdrawnAwardLetters } from "./WithdrawnAwardLetters";
import { withdrawnRoundFor } from "./withdrawnRoundFor";

export const AwardDecisionHistory = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const { data } = useListBidsForEventQuery({ eventId: event.id });
  const {
    data: lettersData,
    isError: lettersFailed,
    isFetching: lettersFetching,
    refetch: refetchLetters,
  } = useGetAwardLettersQuery({ eventId: event.id });

  const history = event.award?.decisionHistory ?? [];
  if (history.length === 0) return null;

  const bids = data?.bids ?? [];
  const supplierName = (bidId: string) => bids.find((b) => b.id === bidId)?.ownerName || t("Unknown");

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
      <Text fontWeight="semibold" fontSize="sm" color="smMuted" textTransform="uppercase" mb="3">
        {t("Previous award decisions")}
      </Text>
      {lettersFailed && (
        <ListLoadError
          message={t("The award letters sent for these decisions could not be loaded.")}
          onRetry={() => void refetchLetters()}
          isRetrying={lettersFetching}
        />
      )}
      <Stack spacing="4">
        {history.map((decision) => (
          <Box key={`${decision.awardedAt}-${decision.revokedAt}`}>
            <HStack spacing="3">
              <Text fontWeight="semibold" color="smText">
                {(decision.bidIds ?? []).map(supplierName).join(", ") || t("Unknown")}
              </Text>
              <Badge colorScheme="red">{t("Revoked")}</Badge>
            </HStack>
            <Text fontSize="sm" color="smMuted">
              {t("Awarded {{awardedDate}} – revoked {{revokedDate}} (evaluation revision {{revision}})", {
                awardedDate: displayDate(decision.awardedAt),
                revokedDate: displayDate(decision.revokedAt),
                revision: decision.evaluationRevision,
              })}
            </Text>
            {decision.revokeReason && (
              <Text fontSize="sm" color="smText" whiteSpace="pre-wrap">
                {t("Reason")}: {decision.revokeReason}
              </Text>
            )}
            <WithdrawnAwardLetters
              event={event}
              round={withdrawnRoundFor(decision.revokedAt, lettersData?.withdrawnRounds ?? [])}
              namesWinnerToOtherBidders={lettersData?.namesWinnerToOtherBidders ?? false}
              standstillDays={decision.standstillDays}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
